import { useCallback, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@/lib/apiEnvelope';

import { AlertModal, Button } from '@components/ui';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import {
  deleteJobTitle,
  getJobTitles,
} from '@/services/jobTitleService';
import { useDepartmentsForCascade } from '@features/departments/hooks/useDepartmentsForCascade';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { JobTitleDto } from '@/types/jobTitle';
import type { FilterFieldConfig } from '@/types/filters';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { buildSortString, toPaginationState, validateTableSearch } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import CreateEditJobTitleModal from '@features/job-titles/CreateEditJobTitleModal';
import ViewJobTitleModal from '@features/job-titles/ViewJobTitleModal';
import { useTranslation } from '@/hooks/useTranslation';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';

import styles from './index.module.scss';

export const Route = createFileRoute('/t/job-titles/')({
  validateSearch: validateTableSearch,
  component: JobTitlesPage,
});

// Cap inline-rendered chips; remainder collapsed into "+N" with tooltip listing the rest.
const INLINE_DEPT_CHIP_LIMIT = 2;

function StatusBadge({ status }: { status: JobTitleDto['status'] }) {
  const className = status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive;
  return <span className={`${styles.badge} ${className}`}>{status}</span>;
}

function JobTitlesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // Department filter options sourced from the cascade hook (active depts only).
  const { departments } = useDepartmentsForCascade();
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ label: d.name, value: d.id })),
    [departments],
  );

  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'departmentIds',
        label: t('K_LABEL_DEPARTMENTS', 'Departments'),
        type: 'multi-select',
        options: departmentOptions,
      },
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')), value: 'INACTIVE' },
        ],
      },
    ],
    [t, departmentOptions],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/t/job-titles',
    filtersParam,
    fields: filterFields,
  });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewItem, setViewItem] = useState<JobTitleDto | null>(null);
  const [editItem, setEditItem] = useState<JobTitleDto | null>(null);
  const [deleteItem, setDeleteItem] = useState<JobTitleDto | null>(null);
  const [auditItem, setAuditItem] = useState<{ id: string; name: string } | null>(null);

  const handleCreate = useCallback(() => setIsCreateOpen(true), []);
  const handleCloseCreate = useCallback(() => setIsCreateOpen(false), []);
  const handleView = useCallback((item: JobTitleDto) => setViewItem(item), []);
  const handleCloseView = useCallback(() => setViewItem(null), []);

  const handleEditFromView = useCallback(() => {
    if (viewItem) {
      setEditItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleDeleteFromView = useCallback(() => {
    if (viewItem) {
      setDeleteItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleAuditFromView = useCallback(() => {
    if (viewItem) {
      setAuditItem({ id: viewItem.id, name: viewItem.name });
      setViewItem(null);
    }
  }, [viewItem]);

  const handleCloseEdit = useCallback(() => setEditItem(null), []);
  const handleCloseDelete = useCallback(() => setDeleteItem(null), []);

  const deleteMutation = useMutation({
    mutationFn: deleteJobTitle,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['job-titles'] });
      void queryClient.invalidateQueries({ queryKey: ['job-titles-cascade'] });
      setDeleteItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (deleteItem) {
      deleteMutation.mutate(deleteItem.id);
    }
  }, [deleteItem, deleteMutation]);

  const columns = useMemo<DataTableColumn<JobTitleDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_NAME', 'Name'),
        sortable: true,
        sortKey: 'name',
        width: '200px',
        cell: (item) => (
          <span className={styles.cellPrimary} title={item.name || '-'}>
            {item.name || '-'}
          </span>
        ),
      },
      {
        id: 'code',
        header: t('K_COLUMN_CODE', 'Code'),
        cell: (item) => <span className={styles.cellSecondary}>{item.code}</span>,
      },
      {
        id: 'departments',
        header: t('K_COLUMN_JOB_TITLE_DEPARTMENTS', 'Departments'),
        cell: (item) => {
          const depts = item.departments ?? [];
          if (depts.length === 0) return <span className={styles.cellSecondary}>—</span>;
          const visible = depts.slice(0, INLINE_DEPT_CHIP_LIMIT);
          const overflow = depts.length - visible.length;
          const tooltip = depts.map((d) => d.name).join(', ');
          return (
            <span className={styles.cellDeptChips} title={tooltip}>
              {visible.map((d) => d.name).join(', ')}
              {overflow > 0 && (
                <span className={styles.cellChipMore}> +{overflow} {t('K_MORE', 'more')}</span>
              )}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
    ],
    [t],
  );

  const rowActions = useCallback(
    (item: JobTitleDto) => {
      const actions: ProfileAction[] = [];
      if (hasPermission('JOB_TITLES_UPDATE') && item.status !== 'INACTIVE') {
        actions.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: () => setEditItem(item) });
      }
      if (hasPermission('JOB_TITLES_DELETE') && item.status === 'ACTIVE') {
        actions.push({
          id: 'deactivate',
          label: t('K_DEACTIVATE', 'Deactivate'),
          onClick: () => setDeleteItem(item),
        });
      }
      if (hasPermission('AUDIT_READ')) {
        actions.push({
          id: 'audit',
          label: t('K_AUDIT_TITLE', 'Audit trail'),
          onClick: () => setAuditItem({ id: item.id, name: item.name }),
        });
      }
      if (actions.length === 0) return null;
      return <ProfileActionsMenu actions={actions} />;
    },
    [t, hasPermission],
  );

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/t/job-titles',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/t/job-titles',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/t/job-titles',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['job-titles', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getJobTitles({ page, size, sort: sortString }, filterPayload),
  });

  const items = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: JobTitleDto) => item.id, []);
  const handleRowClick = useCallback(
    (item: JobTitleDto) => handleView(item),
    [handleView],
  );

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>
            {t('K_EMPTY_NO_JOB_TITLES', 'No job titles yet')}
          </h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_JOB_TITLE', 'Get started by creating your first job title.')}
          </p>
          {hasPermission('JOB_TITLES_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_JOB_TITLE', 'Create Job Title')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_JOB_TITLES', 'Job Titles')}</h1>
          <p className={styles.subtitle}>
            {t('K_PAGE_SUBTITLE_JOB_TITLES', 'Manage job titles and their associated departments.')}
          </p>
        </div>

        {/* Hide Create when the list 403s — see Departments list rationale. */}
        {!listForbidden && hasPermission('JOB_TITLES_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_JOB_TITLE', 'Create Job Title')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={items}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        loadingText={t('K_LOADING_JOB_TITLES', 'Loading job titles...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Job titles list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <ViewJobTitleModal
        isOpen={viewItem !== null}
        onClose={handleCloseView}
        data={viewItem}
        onEdit={handleEditFromView}
        onDelete={handleDeleteFromView}
        onAudit={handleAuditFromView}
      />

      <CreateEditJobTitleModal isOpen={isCreateOpen} onClose={handleCloseCreate} />
      <CreateEditJobTitleModal
        isOpen={editItem !== null}
        onClose={handleCloseEdit}
        editData={editItem}
      />

      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_JOB_TITLE_TITLE', 'Deactivate job title?')}
        description={t(
          'K_CONFIRM_DEACTIVATE_JOB_TITLE',
          'Deactivate "{name}"? Existing employees keep their assignment; new assignments will hide this option.',
        ).replace('{name}', deleteItem?.name ?? '')}
        isOpen={deleteItem !== null}
        onClose={handleCloseDelete}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={handleConfirmDelete}
        loading={deleteMutation.isPending}
      />

      <AuditTrailModal
        open={auditItem !== null}
        onClose={() => setAuditItem(null)}
        filter={{
          resourceType: AUDIT_RESOURCE.JOB_TITLE,
          resourceId: auditItem?.id ?? '',
        }}
        subject={auditItem?.name}
      />
    </div>
  );
}
